import React, { useState } from 'react'
import { useList } from '@refinedev/core'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  IconButton,
  Collapse,
  Alert,
} from '@mui/material'
import { Person, SmartToy, ExpandMore, ExpandLess } from '@mui/icons-material'
import { format } from 'date-fns'
import { UUID } from 'crypto'
import {
  BillingEvent,
  BillingEventType,
  BILLING_EVENT_COLORS,
  BILLING_EVENT_LABELS,
} from '../types/billing'

interface BillingHistoryProps {
  orgUuid: UUID
}

export default function BillingHistory({ orgUuid }: BillingHistoryProps) {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedRow, setExpandedRow] = useState<string | null>(null)

  const { data, isLoading, isError, error } = useList<BillingEvent>({
    resource: 'billing-history',
    pagination: {
      current: page + 1,
      pageSize: rowsPerPage,
    },
    filters: [],
    meta: {
      org_uuid: orgUuid,
      ...(typeFilter !== 'all' && { type_filter: typeFilter }),
      ...(searchTerm && { search: searchTerm.trim() }),
    },
    queryOptions: {
      enabled: !!orgUuid,
      queryKey: ['billing-history', orgUuid, page + 1, rowsPerPage, typeFilter, searchTerm],
      staleTime: 2 * 60 * 1000, // 2 minutes
    },
  })

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD',
    }).format(amount)
  }

  const getEventColor = (eventType: string) => {
    return BILLING_EVENT_COLORS[eventType as BillingEventType] || 'default'
  }

  const getEventLabel = (eventType: string) => {
    return BILLING_EVENT_LABELS[eventType as BillingEventType] || eventType
  }

  const getAmountColor = (impact: number | null | undefined): 'error' | 'success' | undefined => {
    if (impact === null || impact === undefined) return undefined
    return impact > 0 ? 'error' : 'success'
  }

  const handleRowExpand = (eventId: string) => {
    setExpandedRow(expandedRow === eventId ? null : eventId)
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    )
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load billing history: {error?.message || 'Unknown error'}
      </Alert>
    )
  }

  const events = data?.data || []
  const total = data?.total || 0

  // Get unique event types for filter
  const eventTypes = Array.from(new Set(events.map(e => e.event_type)))

  if (events.length === 0 && typeFilter === 'all' && !searchTerm) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Billing History
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No billing events have been recorded for this organization yet.
        </Typography>
      </Paper>
    )
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Billing History
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Timeline of all billing-related events including pauses, resumes, payments, and subscription changes.
      </Typography>

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={typeFilter}
            label="Type"
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            {eventTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {getEventLabel(type)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Search"
          placeholder="Search descriptions"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 250 }}
        />
      </Stack>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={40}></TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Actor</TableCell>
              <TableCell align="right">Financial Impact</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No events found matching your filters
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              events.map((event) => {
                const hasDetails = event.details && Object.keys(event.details).length > 0
                const isExpanded = expandedRow === event.event_id

                return (
                  <React.Fragment key={event.event_id}>
                    <TableRow>
                      <TableCell>
                        {hasDetails && (
                          <IconButton
                            size="small"
                            onClick={() => handleRowExpand(event.event_id)}
                          >
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        {format(new Date(event.event_date), 'dd/MM/yyyy, h:mm a')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getEventLabel(event.event_type)}
                          size="small"
                          sx={{
                            bgcolor: getEventColor(event.event_type),
                            color: 'white',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {event.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          {event.actor_type === 'system' ? (
                            <>
                              <SmartToy fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {event.actor}
                              </Typography>
                            </>
                          ) : (
                            <>
                              <Person fontSize="small" color="action" />
                              <Typography variant="body2">
                                {event.actor}
                              </Typography>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        {event.financial_impact !== null && event.financial_impact !== undefined ? (
                          <Typography
                            variant="body2"
                            color={getAmountColor(event.financial_impact)}
                            fontWeight="medium"
                          >
                            {event.financial_impact > 0 ? '+' : ''}
                            {formatCurrency(event.financial_impact)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    {hasDetails && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 0, borderBottom: isExpanded ? undefined : 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2, px: 3, bgcolor: 'grey.50' }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Event Details
                              </Typography>
                              <Box
                                sx={{
                                  p: 2,
                                  bgcolor: 'white',
                                  borderRadius: 1,
                                  fontFamily: 'monospace',
                                  fontSize: '0.875rem',
                                  border: 1,
                                  borderColor: 'grey.300',
                                }}
                              >
                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                  {JSON.stringify(event.details, null, 2)}
                                </pre>
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                )
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Summary */}
      {events.length > 0 && (
        <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
          <Typography variant="body2" color="textSecondary">
            Showing {events.length} of {total} records
          </Typography>
        </Box>
      )}
    </Box>
  )
}
